import { zodResolver } from "@hookform/resolvers/zod";
import { clsx } from "clsx";
import { Send, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { APIError } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { TEAM_ROLES } from "@/features/onboarding/data";
import { usePermissions } from "@/hooks/use-permissions.ts";
import { useCreateInvite } from "../../hooks/use-create-invite";
import {
	type InviteFormValues,
	type InviteScope,
	inviteSchema,
} from "../../schemas/invite-schema";
import type { Worker } from "../../types";
import { inviteErrorMessage } from "../../utils/invite-error";

const FORM_FIELDS = new Set<keyof InviteFormValues>(["email", "role"]);

const SCOPE_OPTIONS: { value: InviteScope; label: string }[] = [
	{ value: "worksite", label: "Este canteiro" },
	{ value: "org", label: "Organização" },
];

const DEFAULT_VALUES: InviteFormValues = {
	email: "",
	role: "funcionario",
	scope: "worksite",
};

export function InviteDialog({
	worker,
	open,
	onOpenChange,
}: {
	worker?: Worker;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
}) {
	const [internalOpen, setInternalOpen] = useState(false);
	const [formError, setFormError] = useState<string | null>(null);
	const { mutateAsync, isPending } = useCreateInvite();
	const { has } = usePermissions();

	const isOpen = open ?? internalOpen;
	const forWorker = worker != null;

	const {
		register,
		handleSubmit,
		control,
		reset,
		setError,
		setValue,
		watch,
		formState: { errors },
	} = useForm<InviteFormValues>({
		resolver: zodResolver(inviteSchema),
		defaultValues: DEFAULT_VALUES,
	});

	useEffect(() => {
		if (!isOpen) return;
		reset(DEFAULT_VALUES);
		setFormError(null);
	}, [isOpen, reset]);

	const scope = watch("scope");
	const role = watch("role");
	const isOrg = scope === "org";
	const roleOptions = isOrg
		? TEAM_ROLES.filter((option) => option.id !== "cliente")
		: TEAM_ROLES;

	function handleScopeChange(next: InviteScope) {
		setValue("scope", next);
		if (next === "org" && role === "cliente") {
			setValue("role", "funcionario");
		}
	}

	function handleOpenChange(next: boolean) {
		setInternalOpen(next);
		onOpenChange?.(next);
	}

	async function onSubmit(values: InviteFormValues) {
		setFormError(null);
		try {
			await mutateAsync({ ...values, workerId: worker?.id });
			handleOpenChange(false);
		} catch (err) {
			if (err instanceof APIError) {
				let matchedField = false;
				for (const [field, message] of Object.entries(err.violations)) {
					if (FORM_FIELDS.has(field as keyof InviteFormValues)) {
						setError(field as keyof InviteFormValues, { message });
						matchedField = true;
					}
				}
				if (matchedField) return;
			}
			setFormError(
				inviteErrorMessage(
					err,
					"Não foi possível enviar o convite. Tente novamente.",
				),
			);
		}
	}

	if (!has("invites:create")) return null;

	function title(): string {
		if (forWorker) return `Convidar ${worker.name}`;
		return isOrg ? "Convidar para a organização" : "Convidar para o canteiro";
	}

	function description(): string {
		if (forWorker) {
			return "A pessoa recebe o convite por e-mail e, ao aceitar, passa a bater ponto com este mesmo cadastro.";
		}
		return isOrg
			? "A pessoa terá acesso no nível da organização. Define a senha ao aceitar."
			: "Envie um convite por e-mail. A pessoa define a senha ao aceitar.";
	}

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			{!forWorker && (
				<DialogTrigger asChild>
					<Button>
						<UserPlus /> Convidar
					</Button>
				</DialogTrigger>
			)}

			<DialogContent className="invite-dialog">
				<DialogHeader>
					<DialogTitle>{title()}</DialogTitle>
					<DialogDescription>{description()}</DialogDescription>
				</DialogHeader>

				<form
					id="invite-form"
					onSubmit={handleSubmit(onSubmit)}
					className="invite-dialog__form"
				>
					{!forWorker && (
						<div className="invite-dialog__field">
							<Label>Escopo do convite</Label>
							<div className="invite-dialog__scope">
								{SCOPE_OPTIONS.map((option) => (
									<button
										key={option.value}
										type="button"
										onClick={() => handleScopeChange(option.value)}
										aria-pressed={scope === option.value}
										className={clsx(
											"invite-dialog__scope-option",
											scope === option.value && "is-active",
										)}
									>
										{option.label}
									</button>
								))}
							</div>
						</div>
					)}

					<div className="invite-dialog__field">
						<Label htmlFor="invite-email">E-mail</Label>
						<Input
							id="invite-email"
							type="email"
							placeholder="pessoa@empresa.com"
							autoComplete="off"
							{...register("email")}
						/>
						{errors.email && (
							<p className="form-error">{errors.email.message}</p>
						)}
					</div>

					<div className="invite-dialog__field">
						<Label htmlFor="invite-role">Função</Label>
						<Controller
							control={control}
							name="role"
							render={({ field }) => (
								<Select value={field.value} onValueChange={field.onChange}>
									<SelectTrigger
										id="invite-role"
										className="invite-dialog__select"
									>
										<SelectValue placeholder="Selecione uma função" />
									</SelectTrigger>
									<SelectContent>
										{roleOptions.map((option) => {
											const Icon = option.icon;
											return (
												<SelectItem key={option.id} value={option.id}>
													<Icon />
													<span>{option.label}</span>
												</SelectItem>
											);
										})}
									</SelectContent>
								</Select>
							)}
						/>
						{errors.role && <p className="form-error">{errors.role.message}</p>}
					</div>

					{formError && (
						<p role="alert" className="invite-dialog__error">
							{formError}
						</p>
					)}
				</form>

				<DialogFooter>
					<DialogClose asChild>
						<Button variant="outline" disabled={isPending}>
							Cancelar
						</Button>
					</DialogClose>
					<Button type="submit" form="invite-form" disabled={isPending}>
						<Send /> Enviar convite
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
